import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Mail, ExternalLink } from 'lucide-react';
import ShareButtons from './ShareButtons';
import GiftPromoCard from './GiftPromoCard';

const CABRINI_IMG = "https://customer-assets.emergentagent.com/job_988ca3c4-0353-4296-b3e4-3dfc4a85f5c2/artifacts/91jkfvp8_Cabrini%20with%20hand%20on%20chin.png";

/* ──────────────────────────────────────────────
   EDIT THESE with Cabrini's real social handles.
   Leave as '#' to hide the button gracefully.
   ────────────────────────────────────────────── */
const SOCIAL = {
  instagram: '#',
  facebook: '#',
  youtube: '#',
  tiktok: '#',
  pinterest: 'https://www.pinterest.com/alchemyvillage/',
  email: 'mailto:hello@thealchemyvillage.com',
};

const TRUST_STATS = [
  { value: '40+', label: 'Years of lived wisdom' },
  { value: '1', label: 'Stage shared with Les Brown' },
  { value: '6', label: 'Paths of transformation' },
  { value: '∞', label: 'Women ready to rise' },
];

function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

function PinterestIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 19l3-7" />
      <path d="M12 7a3 3 0 0 1 3 3c0 2-2 4-4 4" />
    </svg>
  );
}

const SOCIAL_BUTTONS = [
  { key: 'instagram', label: 'Instagram', href: SOCIAL.instagram, Icon: Instagram },
  { key: 'facebook', label: 'Facebook', href: SOCIAL.facebook, Icon: Facebook },
  { key: 'pinterest', label: 'Pinterest', href: SOCIAL.pinterest, Icon: PinterestIcon },
  { key: 'youtube', label: 'YouTube', href: SOCIAL.youtube, Icon: Youtube },
  { key: 'tiktok', label: 'TikTok', href: SOCIAL.tiktok, Icon: TikTokIcon },
  { key: 'email', label: 'Email', href: SOCIAL.email, Icon: Mail },
];

export default function AboutPage() {
  return (
    <section data-testid="about-page" className="about-page">
      {/* Hero */}
      <div className="about-hero">
        <div className="about-hero-image">
          <img src={CABRINI_IMG} alt="Cabrini Schnyder" data-testid="about-photo" />
        </div>
        <div className="about-hero-text">
          <p className="about-label">Founder · Teacher · GangstaChef</p>
          <h1 data-testid="about-title" className="about-title">Cabrini Schnyder</h1>
          <p className="about-lead">
            Lifelong entrepreneur, creator, and transformational teacher who has spent over four decades
            building businesses, guiding others, and turning real-life experiences into wisdom that helps
            people reclaim their lives.
          </p>
          <div className="about-hero-ctas">
            <Link to="/quiz" className="village-btn village-btn--primary" data-testid="about-cta-quiz">
              Discover Your Archetype
            </Link>
            <Link to="/book-cabrini" className="village-btn village-btn--secondary" data-testid="about-cta-book">
              Book Cabrini to Speak
            </Link>
          </div>
        </div>
      </div>

      {/* Trust Strip */}
      <div className="about-trust" data-testid="about-trust-strip">
        {TRUST_STATS.map((s) => (
          <div key={s.label} className="about-trust-stat">
            <span className="about-trust-value">{s.value}</span>
            <span className="about-trust-label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="about-body">
        {/* My Story — personal, first-person */}
        <div className="about-section">
          <h2 className="about-section-title">My Story</h2>
          <p className="about-text">
            I didn't arrive here in a straight line. I arrived through decades of decisions —
            some brilliant, some brutal — raising myself as much as raising others,
            cooking my way through grief, building businesses through doubt, and teaching
            the thing I most needed to learn.
          </p>
          <p className="about-text">
            Somewhere between kitchens, stages, studios, and quiet mornings with my own reflection,
            I discovered something nobody handed me: the greatest guide I would ever have was already
            inside me. And if it was inside me, it was inside you too.
          </p>
          <p className="about-text">
            The Alchemy Village is what that discovery looks like when it grows up.
            It's a home for women 50+ who are done performing and ready to become.
          </p>
        </div>

        {/* The Philosophy */}
        <div className="about-section">
          <h2 className="about-section-title">The Philosophy</h2>
          <p className="about-text">
            I'm the creator of <strong>Cabrini's Law of F.U.C.K. T.H.A.T.T.</strong> —
            Finally Understanding Common Knowledge: Things Happen All The Time. A philosophy designed to help
            people stop sleep-walking through life and start making conscious decisions about who they are,
            what they want, and why they want it.
          </p>
          <p className="about-text">
            Through my work, I remind people that nothing is wrong with them — they simply need the tools to
            understand how their thoughts, feelings, and decisions shape their reality.
          </p>
          <p className="about-text">
            Known to many as <strong>GangstaChef Cabrini</strong>, I blend my background in cooking, entrepreneurship,
            spirituality, and personal development to teach what I call "Food for Thought" — nourishment for the mind,
            body, and soul.
          </p>
        </div>

        {/* Credentials */}
        <div className="about-credentials">
          <h2 className="about-section-title">Also a…</h2>
          <div className="about-roles">
            <span className="about-role">Author</span>
            <span className="about-role">Speaker (shared stage with Les Brown)</span>
            <span className="about-role">Life Coach & teacher of self-creation</span>
            <span className="about-role">Music producer · founder of The Alchemist Music (TAM)</span>
            <span className="about-role">Creator of FTU — F.U.C.K. T.H.A.T.T. University</span>
          </div>
        </div>

        {/* The Village */}
        <div className="about-section">
          <h2 className="about-section-title">Why I Built the Village</h2>
          <p className="about-text">
            After decades of helping others transform their lives, I built <strong>The Alchemy Village (TAV)</strong> —
            a place designed especially for people 50+ who are asking one powerful question:
          </p>
          <p className="about-highlight">"Now what?"</p>
          <p className="about-text">
            The Alchemy Village is more than a community. It's a living ecosystem where people come to rediscover themselves
            through conversation, creativity, music, teaching, cooking, spiritual reflection, and personal responsibility.
          </p>
        </div>

        {/* Three Questions */}
        <div className="about-section about-section--centered">
          <h2 className="about-section-title">Three Questions That Change Everything</h2>
          <div className="about-questions">
            <span>Who am I?</span>
            <span>What do I want?</span>
            <span>Why do I want it?</span>
          </div>
        </div>

        {/* Five Principles */}
        <div className="about-section about-section--centered">
          <h2 className="about-section-title">Five Principles I Live By</h2>
          <div className="about-principles">
            <span className="about-principle">Make a Decision</span>
            <span className="about-principle">Trust Your Decision</span>
            <span className="about-principle">Stay Focused</span>
            <span className="about-principle">Take Inspired Action</span>
            <span className="about-principle">Enjoy the Journey</span>
          </div>
        </div>

        {/* Mission */}
        <div className="about-section about-section--centered">
          <h2 className="about-section-title">My Mission</h2>
          <p className="about-mission">
            To help people wake up from the life they've been living and consciously create the life they actually want.
          </p>
          <p className="about-text" style={{ textAlign: 'center' }}>
            The answers you seek have always been within you.
          </p>
        </div>

        {/* Closing Quote */}
        <blockquote className="about-quote" data-testid="about-quote">
          "The greatest guide you'll ever have is...<br />
          The God in YOU!"
          <cite>— Cabrini</cite>
        </blockquote>

        {/* Connect with Cabrini */}
        <div className="about-connect" data-testid="about-connect">
          <h2 className="about-section-title">Connect with Cabrini</h2>
          <p className="about-text" style={{ textAlign: 'center' }}>
            Follow along, drop a message, or tag me when the Village shows up in your life.
          </p>
          <div className="about-social-row">
            {SOCIAL_BUTTONS.filter((s) => s.href && s.href !== '#').map(({ key, label, href, Icon }) => (
              <a
                key={key}
                href={href}
                target={key === 'email' ? '_self' : '_blank'}
                rel="noopener noreferrer"
                className="about-social-btn"
                data-testid={`social-${key}`}
                aria-label={label}
              >
                <Icon size={20} />
                <span>{label}</span>
                <ExternalLink size={12} className="about-social-out" />
              </a>
            ))}
            {SOCIAL_BUTTONS.every((s) => !s.href || s.href === '#') && (
              <p className="about-social-placeholder">Social links coming soon.</p>
            )}
          </div>
        </div>

        {/* Share */}
        <ShareButtons
          url="https://thealchemyvillage.com/about"
          title="Cabrini Schnyder — Founder of The Alchemy Village"
          text="Lifelong entrepreneur, GangstaChef, and teacher of self-creation. Meet the woman behind The Alchemy Village."
          image={CABRINI_IMG}
          variant="gold"
        />

        {/* Free chapter gift */}
        <GiftPromoCard />

        {/* CTA */}
        <div className="about-cta">
          <Link to="/quiz" className="village-btn village-btn--primary">
            Discover Your Archetype
          </Link>
          <Link to="/life-map" className="village-btn village-btn--secondary">
            Explore the Village
          </Link>
        </div>
      </div>
    </section>
  );
}
